import React from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Empty, Spin } from 'antd';
import { DollarOutlined, UserOutlined, AlertOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatConsumption } from '../utils/formatters';

const DashboardPage = () => {
  const { loading, getStatistics, getUnpaidInvoices, subscribers, invoices, payments } = useApp();

  if (loading) return <Spin />;

  const stats = getStatistics();
  const unpaidInvoices = getUnpaidInvoices();

  const recentPayments = payments.slice(-5).reverse();

  const paymentColumns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => new Date(date).toLocaleDateString('fr-FR')
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Méthode',
      dataIndex: 'method',
      key: 'method'
    }
  ];

  const unpaidColumns = [
    {
      title: 'Numéro facture',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber'
    },
    {
      title: 'Abonné',
      dataIndex: 'subscriberName',
      key: 'subscriberName'
    },
    {
      title: 'Montant restant',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: (amount) => <span style={{ color: 'red' }}>{formatCurrency(amount)}</span>
    },
    {
      title: 'Date d\'échéance',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => {
        const dueDate = new Date(date);
        const now = new Date();
        const isOverdue = now > dueDate;
        return (
          <Tag color={isOverdue ? 'red' : 'orange'}>
            {dueDate.toLocaleDateString('fr-FR')}
          </Tag>
        );
      }
    }
  ];

  return (
    <div>
      <h1>Tableau de bord</h1>

      <Row gutter={[16, 16]} style={{ marginBottom: 30 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Abonnés"
              value={stats.totalSubscribers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Revenus du mois"
              value={stats.totalRevenue}
              suffix=" FCFA"
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Impayés"
              value={stats.unpaidAmount}
              suffix=" FCFA"
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Factures payées"
              value={stats.paidInvoices}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Factures en attente de paiement" size="large">
            {unpaidInvoices.length > 0 ? (
              <Table
                columns={unpaidColumns}
                dataSource={unpaidInvoices.slice(0, 5)}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="Aucune facture impayée" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Paiements récents" size="large">
            {recentPayments.length > 0 ? (
              <Table
                columns={paymentColumns}
                dataSource={recentPayments}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="Aucun paiement" />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} sm={12}>
          <Card title="Résumé des factures">
            <p>Factures émises: <strong>{invoices.length}</strong></p>
            <p>Factures payées: <strong>{stats.paidInvoices}</strong></p>
            <p>Factures impayées: <strong>{stats.unpaidInvoices}</strong></p>
          </Card>
        </Col>

        <Col xs={24} sm={12}>
          <Card title="Consommation totale">
            <p>Total consommé: <strong>{formatConsumption(stats.totalConsumption)} m³</strong></p>
            <p>Nombre de relevés: <strong>{stats.totalConsumption}</strong></p>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
